import type { PocInputValidity } from 'commons/types';
import { Input } from 'commons/components';
import { useTranslation } from 'core/i18n/i18n';
import { CustomValidationCode } from 'core/utils/backend-service';
import React from 'react';
import { OnChangeAutomationRuleStateData } from '../types/automation-rules.type';

const valueMissingValidation: PocInputValidity = {
    state: 'error',
    type: ['value-missing'],
};

const defaultValidation: PocInputValidity = {
    state: 'valid',
};

interface AutomationRulesInputProps {
    name: string;
    defaultValue?: string;
    type: string;
    placeholder?: string;
    isAutofocus?: boolean;
    customValidity?: PocInputValidity;
    customValidationCode?: CustomValidationCode;
    onChange: (data: OnChangeAutomationRuleStateData) => void;
}

export const AutomationRulesInput: React.FC<AutomationRulesInputProps> = (props) => {
    const {
        name,
        defaultValue = '',
        type,
        placeholder = '',
        isAutofocus,
        customValidity,
        customValidationCode,
        onChange,
    } = props;

    const [text, setText] = React.useState<string | null>(defaultValue);
    const [validity, setValidity] = React.useState<PocInputValidity>(structuredClone(defaultValidation));

    const { t } = useTranslation();

    const onValueChange = (newText: string | null) => {
        const data = { key: name, value: newText ?? '', type };
        onChange(data);
        setText(newText);

        setValidity(newText ? defaultValidation : valueMissingValidation);
    };

    React.useEffect(() => {
        if (customValidity) {
            setValidity(customValidity);
        }
    }, [customValidity]);

    const duplicatedAlertMessage = {
        error: {
            duplicated: t('settings.system.automation-rules.actions.enable.notification.duplicated'),
        },
    };

    return (
        <Input
            name={name}
            validity={validity}
            noClearIcon={false}
            value={text ?? undefined}
            placeholder={placeholder}
            maxlength={60}
            onChange={(_, newText) => onValueChange(newText)}
            autofocus={isAutofocus}
            validationMessages={duplicatedAlertMessage}
            floatingAssistiveText
            customValidationCode={customValidationCode}
        />
    );
};
